import { Component, EventEmitter, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';

import { OrderManagementViewService } from '@core/api/api';
import { User } from '@core/api/model/User';
import { AuthService } from '@core/auth-service/auth.service';
import { ResponsiveService } from '@core/responsive.service';
import { AlertService } from '@shared/alert.service';
import { TranslatePluralPipe } from '@shared/translate-plural.pipe';
import { ContactDialogComponent } from '@widgets/navigation/contact-dialog/contact-dialog.component';
import { ImprintDialogComponent } from '@widgets/navigation/imprint-dialog/imprint-dialog.component';

import { SideMenuService } from './side-menu.service';

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
  providers: [SideMenuService],
  standalone: false,
})
export class SideMenuComponent implements OnInit, OnDestroy {
  @Output() closed = new EventEmitter();

  private readonly authService = inject(AuthService);
  private readonly translatePlural = inject(TranslatePluralPipe);
  private readonly sideMenuService = inject(SideMenuService);
  private readonly responsiveService = inject(ResponsiveService);
  private readonly orderManagementViewService = inject(OrderManagementViewService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly alertService = inject(AlertService);

  public minimized: boolean;
  public currentUser: User | null = null;

  private destroy$ = new Subject();

  constructor() {
    const sideMenuService = this.sideMenuService;

    this.minimized = sideMenuService.minimized.value;
  }

  ngOnInit(): void {
    this.authService.userIsLoggedIn$.pipe(takeUntil(this.destroy$)).subscribe((loggedIn) => {
      if (!loggedIn) {
        this.closed.emit();
      }
    });

    this.authService.loggedInUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.currentUser = user;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  async logout() {
    let doLogout = true;

    if (!(await firstValueFrom(this.responsiveService.isMobilePortrait$))) {
      try {
        const unsentOrders = await firstValueFrom(
          this.orderManagementViewService.getUnsentOrders(),
        );
        if (this.currentUser && unsentOrders.length > 0) {
          const ignoreUnsentOrders = await this.askForUnsentOrders(unsentOrders.length);
          if (!ignoreUnsentOrders) {
            await this.router.navigate(['/orders']);
            doLogout = false;
          }
        }
      } catch (e) {
        console.error('', e);
      }
    }

    if (doLogout) {
      this.doLogout();
    }
  }

  close() {
    this.closed.emit();
  }

  toggleMinimize() {
    this.minimized = !this.minimized;
    this.sideMenuService.minimized.next(this.minimized);
  }

  openContactDialog() {
    this.dialog.open(ContactDialogComponent, {
      data: { userEmail: this.currentUser?.email ?? '' },
      minWidth: '65vh',
      disableClose: true,
    });
  }

  openImprint() {
    this.dialog.open(ImprintDialogComponent);
  }

  private doLogout() {
    this.close();
    this.authService.logout();
  }

  private askForUnsentOrders(unsentOrdersCount: number) {
    if (!this.currentUser) {
      return;
    }
    const args = {
      days: this.currentUser.unsentOrdersNotificationPeriod,
      count: unsentOrdersCount,
    };

    return firstValueFrom(
      this.alertService.confirmation(
        this.translatePlural.transform('login.UNSENT_ORDERS_NOTIFICATION', args),
      ),
    );
  }
}
